import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Clock, X } from "lucide-react";
import { API } from "../lib/api";

const RECENT_KEY = "aurum_recent_search";
const RECENT_MAX = 8;

const typeColor = {
    stock: "var(--cyan)",
    etf: "var(--amber)",
    crypto: "var(--green)",
    forex: "var(--magenta)",
    commodity: "var(--yellow)",
    index: "var(--text-dim)",
};

const typeLabel = {
    stock: "Azione", etf: "ETF", crypto: "Crypto",
    forex: "Forex", commodity: "Commodity", index: "Indice",
};

const FILTERS = [
    { id: "all", label: "Tutti" },
    { id: "stock", label: "Azioni" },
    { id: "etf", label: "ETF" },
    { id: "crypto", label: "Crypto" },
    { id: "forex", label: "Forex" },
    { id: "commodity", label: "Commodities" },
    { id: "index", label: "Indici" },
];

export const SearchBar = () => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");
    const [recent, setRecent] = useState([]);
    const [active, setActive] = useState(0);
    const containerRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        try {
            const r = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
            setRecent(r);
        } catch (err) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("Failed to load recent searches:", err);
            }
        }
    }, []);

    useEffect(() => {
        if (!q.trim()) { setResults([]); return; }
        const ctrl = new AbortController();
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get(`${API}/search`, { params: { q, limit: 30 }, signal: ctrl.signal });
                setResults(data);
                setActive(0);
            } catch (err) {
                if (err.name !== "CanceledError" && process.env.NODE_ENV !== "production") {
                    console.warn("Search request failed:", err);
                }
            }
        }, 150);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [q]);

    useEffect(() => {
        const onDoc = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const filtered = filter === "all" ? results : results.filter(r => r.type === filter);

    const pick = useCallback((item) => {
        const next = [item, ...recent.filter(r => r.symbol !== item.symbol)].slice(0, RECENT_MAX);
        setRecent(next);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        setOpen(false);
        setQ("");
        nav(`/asset/${encodeURIComponent(item.symbol)}`);
    }, [recent, nav]);

    const clearRecent = (e) => {
        e.stopPropagation();
        setRecent([]);
        localStorage.removeItem(RECENT_KEY);
    };

    const onKey = (e) => {
        if (!open) return;
        const list = q.trim() ? filtered : recent;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive(a => Math.min(a + 1, list.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive(a => Math.max(a - 1, 0));
        } else if (e.key === "Enter" && list[active]) {
            e.preventDefault();
            pick(list[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const showList = open && (q.trim() ? filtered.length > 0 : recent.length > 0);

    return (
        <div ref={containerRef} className="relative w-full max-w-md">
            <div className="flex items-center gap-2 border border-soft bg-elev2 px-2.5 py-1.5 focus-within:border-amber">
                <Search size={14} className="text-mute" />
                <input
                    data-testid="search-input"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                    placeholder="Cerca simbolo, azienda, crypto…"
                    className="bg-transparent outline-none text-sm flex-1 placeholder:text-mute"
                />
                {q && (
                    <button onClick={() => setQ("")} className="text-mute hover:text-foreground">
                        <X size={12} />
                    </button>
                )}
                <kbd className="text-[9px] text-mute border border-soft px-1 hidden md:inline">⌘K</kbd>
            </div>

            {open && (
                <div data-testid="search-dropdown" className="absolute top-full left-0 right-0 mt-1 border border-strong bg-elev shadow-xl z-50 max-h-[70vh] overflow-y-auto">
                    {/* Filter chips */}
                    {q.trim() && (
                        <div className="flex gap-1 p-2 border-b border-soft overflow-x-auto">
                            {FILTERS.map(f => (
                                <button
                                    key={f.id}
                                    onClick={() => setFilter(f.id)}
                                    className={`px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${filter === f.id ? "text-amber border border-amber" : "text-dim border border-transparent hover:text-foreground"}`}
                                    style={filter === f.id ? { borderColor: "var(--amber)", color: "var(--amber)" } : undefined}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Results */}
                    {showList ? (
                        <div className="py-1">
                            {!q.trim() && (
                                <div className="flex items-center justify-between px-3 py-1.5 text-[10px] text-mute tracking-widest">
                                    <span className="flex items-center gap-1"><Clock size={10} /> RECENTI</span>
                                    <button onClick={clearRecent} className="hover:text-foreground">PULISCI</button>
                                </div>
                            )}
                            {(q.trim() ? filtered : recent).map((it, i) => (
                                <button
                                    key={it.symbol}
                                    data-testid={`search-result-${it.symbol}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => pick(it)}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left ${active === i ? "bg-elev2" : ""} hover:bg-elev2`}
                                >
                                    <span className="font-semibold text-amber tnum w-24 truncate">{it.symbol}</span>
                                    <span className="text-dim text-xs flex-1 truncate">{it.name}</span>
                                    <span
                                        className="text-[9px] tracking-widest font-semibold uppercase px-1.5 py-0.5 border border-soft"
                                        style={{ color: typeColor[it.type] || "var(--text-dim)" }}
                                    >
                                        {typeLabel[it.type] || it.type}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="px-4 py-6 text-center text-mute text-xs">
                            {q.trim() ? `Nessun risultato per "${q}"` : "Inizia a digitare per cercare oltre 600 asset"}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
